import React from "react";
import { Field, useFormikContext } from "formik";
import { Col, Form, Row } from "react-bootstrap";
import { AddressFormSection, AddressLookupProvider, AddressPanel } from "../../components/UI";
import { useViewport } from "../../contexts";
import type { ISiteOption } from "../../customTypings/tipper/Tipper";
import { shouldShowFormFieldError } from "../../utils";

export interface TipperEditFormGridProps {
  showSiteField: boolean;
  isNew: boolean;
  metaDeleted: boolean;
  siteId: string;
  setSiteId: (siteId: string) => void;
  siteDisplayName: string;
  sites: ISiteOption[];
  liveValidationAfterSubmit: boolean;
  errors: { name?: string; carrierLicenceNumber?: string };
  touched: { name?: boolean; carrierLicenceNumber?: boolean };
}

function GridFieldCell({ children }: { children: React.ReactNode }) {
  return <div className="mb-3">{children}</div>;
}

function TipperSiteField({
  showSiteField,
  isNew,
  metaDeleted,
  siteId,
  setSiteId,
  siteDisplayName,
  sites,
}: Pick<
  TipperEditFormGridProps,
  "showSiteField" | "isNew" | "metaDeleted" | "siteId" | "setSiteId" | "siteDisplayName" | "sites"
>) {
  if (!showSiteField) return null;

  return isNew ? (
    <Form.Group>
      <Form.Label>Site</Form.Label>
      <Form.Select
        value={siteId}
        onChange={(e) => setSiteId(e.target.value)}
        disabled={metaDeleted}
        aria-label="Site"
        required
      >
        <option value="">Select a site…</option>
        {sites.map((s) => (
          <option key={s.id} value={s.id}>
            {s.siteName}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  ) : (
    <Form.Group>
      <Form.Label>Site</Form.Label>
      <Form.Control readOnly disabled value={siteDisplayName} />
    </Form.Group>
  );
}

function TipperNameField({
  metaDeleted,
  liveValidationAfterSubmit,
  errors,
  touched,
}: Pick<TipperEditFormGridProps, "metaDeleted" | "liveValidationAfterSubmit" | "errors" | "touched">) {
  return (
    <Form.Group>
      <Form.Label>Name</Form.Label>
      <Field
        name="name"
        className={`form-control${shouldShowFormFieldError("name", errors, touched, liveValidationAfterSubmit) ? " is-invalid" : ""}`}
        disabled={metaDeleted}
      />
      {shouldShowFormFieldError("name", errors, touched, liveValidationAfterSubmit) && (
        <div className="invalid-feedback d-block">{errors.name}</div>
      )}
    </Form.Group>
  );
}

function TipperCarrierLicenceField({
  metaDeleted,
  liveValidationAfterSubmit,
  errors,
  touched,
}: Pick<TipperEditFormGridProps, "metaDeleted" | "liveValidationAfterSubmit" | "errors" | "touched">) {
  const { setFieldValue } = useFormikContext();

  return (
    <Form.Group>
      <Form.Label className="font-monospace">Carrier licence</Form.Label>
      <Field
        name="carrierLicenceNumber"
        className={`form-control font-monospace text-uppercase${shouldShowFormFieldError("carrierLicenceNumber", errors, touched, liveValidationAfterSubmit) ? " is-invalid" : ""}`}
        disabled={metaDeleted}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          void setFieldValue("carrierLicenceNumber", e.target.value.toUpperCase());
        }}
      />
      {shouldShowFormFieldError("carrierLicenceNumber", errors, touched, liveValidationAfterSubmit) && (
        <div className="invalid-feedback d-block">{errors.carrierLicenceNumber}</div>
      )}
    </Form.Group>
  );
}

function TipperLicenceExpiryField({ metaDeleted }: Pick<TipperEditFormGridProps, "metaDeleted">) {
  return (
    <Form.Group>
      <Form.Label>Licence expiry</Form.Label>
      <Field type="date" name="licenceExpiryDate" className="form-control" disabled={metaDeleted} />
    </Form.Group>
  );
}

function TipperEnabledCheckbox() {
  return (
    <div className="form-check">
      <Field id="tipper-is-enabled" name="isEnabled" type="checkbox" className="form-check-input" />
      <label className="form-check-label" htmlFor="tipper-is-enabled">
        Enabled
      </label>
    </div>
  );
}

function TipperAddressPanel({ layoutVariant, postcodeLayout }: { layoutVariant: "pairedRows" | "stacked"; postcodeLayout: "below" | "separate" }) {
  return (
    <AddressPanel className="mb-3">
      <AddressFormSection layoutVariant={layoutVariant} postcodeLayout={postcodeLayout} requiredFields={[]} />
    </AddressPanel>
  );
}

/** Large: left tipper fields, right address panel. */
export function TipperEditFormGridLarge(props: TipperEditFormGridProps) {
  return (
    <div className="vstack gap-2">
      <Row className="g-3">
        <Col lg={4}>
          <div className="vstack gap-2">
            {props.showSiteField && (
              <GridFieldCell>
                <TipperSiteField {...props} />
              </GridFieldCell>
            )}
            <GridFieldCell>
              <TipperNameField {...props} />
            </GridFieldCell>
            <GridFieldCell>
              <TipperCarrierLicenceField {...props} />
            </GridFieldCell>
            <GridFieldCell>
              <TipperLicenceExpiryField metaDeleted={props.metaDeleted} />
            </GridFieldCell>
            {props.isNew && (
              <GridFieldCell>
                <TipperEnabledCheckbox />
              </GridFieldCell>
            )}
          </div>
        </Col>
        <Col lg={8}>
          <TipperAddressPanel layoutVariant="pairedRows" postcodeLayout="below" />
        </Col>
      </Row>
    </div>
  );
}

/** Medium: left tipper fields, right stacked address panel. */
export function TipperEditFormGridMedium(props: TipperEditFormGridProps) {
  return (
    <div className="vstack gap-2">
      <Row className="g-3">
        <Col md={6}>
          <div className="vstack gap-2">
            {props.showSiteField && (
              <GridFieldCell>
                <TipperSiteField {...props} />
              </GridFieldCell>
            )}
            <GridFieldCell>
              <TipperNameField {...props} />
            </GridFieldCell>
            <GridFieldCell>
              <TipperCarrierLicenceField {...props} />
            </GridFieldCell>
            <GridFieldCell>
              <TipperLicenceExpiryField metaDeleted={props.metaDeleted} />
            </GridFieldCell>
            {props.isNew && (
              <GridFieldCell>
                <TipperEnabledCheckbox />
              </GridFieldCell>
            )}
          </div>
        </Col>
        <Col md={6}>
          <TipperAddressPanel layoutVariant="stacked" postcodeLayout="below" />
        </Col>
      </Row>
    </div>
  );
}

/**
 * Narrow: Licence expiry, Name, Carrier licence, then address panel, then Enabled (new only).
 */
export function TipperEditFormGridNarrow(props: TipperEditFormGridProps) {
  return (
    <>
      {props.showSiteField && (
        <div className="mb-3">
          <TipperSiteField {...props} />
        </div>
      )}
      <div className="mb-3">
        <TipperLicenceExpiryField metaDeleted={props.metaDeleted} />
      </div>
      <div className="mb-3">
        <TipperNameField {...props} />
      </div>
      <div className="mb-3">
        <TipperCarrierLicenceField {...props} />
      </div>
      <TipperAddressPanel layoutVariant="stacked" postcodeLayout="separate" />
      {props.isNew && (
        <div className="mb-3">
          <TipperEnabledCheckbox />
        </div>
      )}
    </>
  );
}

export function TipperEditFormGrid(props: TipperEditFormGridProps) {
  const { width } = useViewport();

  return (
    <AddressLookupProvider>
      {width >= 992 ? (
        <TipperEditFormGridLarge {...props} />
      ) : width >= 768 ? (
        <TipperEditFormGridMedium {...props} />
      ) : (
        <TipperEditFormGridNarrow {...props} />
      )}
    </AddressLookupProvider>
  );
}
